// src/redux/features/subscriptionSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { addDoc, collection, query, where, getDocs, updateDoc, orderBy } from 'firebase/firestore';
import { sendSubriberWelcomeEmail } from './emailSlice';
import { db } from '../../firebase/config';

const subscribersCollection = collection(db, 'subscribers');

export const fetchSubscribers = createAsyncThunk(
    'subscription/fetchSubscribers',
    async (_, { rejectWithValue }) => {
        try {
            const q = query(
                subscribersCollection,
                orderBy('createdAt', 'desc')
            );
            
            const querySnapshot = await getDocs(q);
            const subscribers = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: {
                    seconds: doc.data().createdAt.seconds,
                    nanoseconds: doc.data().createdAt.nanoseconds
                },
                unsubscribedAt: doc.data().unsubscribedAt ? {
                    seconds: doc.data().unsubscribedAt.seconds,
                    nanoseconds: doc.data().unsubscribedAt.nanoseconds
                } : null
            }));

            return subscribers;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);
// Async thunk for subscribing
export const subscribeEmail = createAsyncThunk(
    'subscription/subscribe',
    async (email, { rejectWithValue, dispatch }) => {
        try {
            // Check if email already exists
            const q = query(
                subscribersCollection,
                where('email', '==', email),
                where('isActive', '==', true)
            );
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                throw new Error('Email already subscribed');
            }

            // Add new subscription
            const docRef = await addDoc(subscribersCollection, {
                email,
                createdAt: new Date(),
                isActive: true
            });

            if (docRef) {
                // Dispatch the welcome email
                dispatch(sendSubriberWelcomeEmail({ subscriber: { email } }));
            }

            return { id: docRef.id, email, createdAt: new Date().toISOString() };
        } catch (error) {
            console.log(error);
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for unsubscribing
export const unsubscribeEmail = createAsyncThunk(
    'subscription/unsubscribe',
    async (email, { rejectWithValue, dispatch }) => {
        try {
            const q = query(
                subscribersCollection,
                where('email', '==', email),
                where('isActive', '==', true)
            );
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                throw new Error('Email not found in subscription list');
            }

            // Update the subscriber document
            const docRef = querySnapshot.docs[0].ref;
            await updateDoc(docRef, {
                isActive: false,
                unsubscribedAt: new Date()
            });

            dispatch(fetchSubscribers());

            return email;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const subscriptionSlice = createSlice({
    name: 'subscription',
    initialState: {
        subscribers: [],
        status: 'idle', // idle, loading, succeeded, failed
        error: null,
        subscribeStatus: 'idle',
        unsubscribeStatus: 'idle'
    },
    reducers: {
        resetStatus: (state) => {
            state.status = 'idle';
            state.error = null;
            state.subscribeStatus = 'idle';
            state.unsubscribeStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch subscribers cases
            .addCase(fetchSubscribers.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchSubscribers.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.subscribers = action.payload;
            })
            .addCase(fetchSubscribers.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Subscribe cases
            .addCase(subscribeEmail.pending, (state) => {
                state.subscribeStatus = 'loading';
                state.error = null;
            })
            .addCase(subscribeEmail.fulfilled, (state, action) => {
                state.subscribeStatus = 'succeeded';
                state.subscribers.push(action.payload);
            })
            .addCase(subscribeEmail.rejected, (state, action) => {
                state.subscribeStatus = 'failed';
                state.error = action.payload;
            })
            // Unsubscribe cases
            .addCase(unsubscribeEmail.pending, (state) => {
                state.unsubscribeStatus = 'loading';
                state.error = null;
            })
            .addCase(unsubscribeEmail.fulfilled, (state, action) => {
                state.unsubscribeStatus = 'succeeded';
                state.subscribers = state.subscribers.filter(
                    sub => sub.email !== action.payload
                );
            })
            .addCase(unsubscribeEmail.rejected, (state, action) => {
                state.unsubscribeStatus = 'failed';
                state.error = action.payload;
            });
    }
});

export const { resetStatus } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
